import axios from "axios";
import { csvParse } from "d3-dsv";
import { group, rollups } from "d3-array";
import sortBy from "lodash.sortby";

import mapToNest from "./mapToNest.js";
import {
  getCountryByCaps,
  getCountryById,
  getCountryByName,
} from "./countryLookup.js";

const data = [
  {
    key: "investments",
    // file: 'China FDI & China DFI 20200609.tsv',
    //file: 'China FDI & China DFI_pitch_102022.csv',
    file: "CDP_20250504.csv",
  },
  {
    key: "companies",
    file: "List of company and keywords for matching.tsv",
  },
];

const metricMap = {
  "Capacity (MW)": {
    key: "capacity",
    type: "value",
  },
  "Project Status": {
    key: "status",
    type: "category",
  },
  Technology: {
    key: "technology",
    type: "category",
  },
  "Estimated Annual CO2 Emissions from Power Generation (1000 ton)": {
    key: "co2emissions",
    type: "value",
  },
  Lender: {
    key: "lender",
    type: "category",
  },
  "Deal Type": {
    key: "dealtype",
    type: "category",
  },
  "Year of Commission (Acquisition year for M&A)": {
    key: "year",
    type: "category",
  },
};

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

class DataLoader {
  load() {
    this._data = {};
    return new Promise((resolve, reject) => {
      Promise.all(
        data.map((d) => {
          return new Promise((resolve, reject) => {
            const url = `${process.env.PUBLIC_URL}/data/${d.file}`;
            axios.get(url).then((response) => {
              const rows = csvParse(response.data);
              this._data[d.key] = rows;
              resolve(rows);
            });
          });
        })
      ).then((value) => {
        this._data.investments = this._data.investments.map(
          (d, initialIndex) => {
            const country =
              getCountryByCaps(d.Country) || getCountryByName(d.Country);
            if (!country) {
              console.log(`Country not found "${d.Country}"`);
            }
            d.State = d["State/Province"];

            const stateID = `${d["State/Province"]}-${country.name}`.replace(
              / /g,
              "-"
            );
            const plant = d["Plant Name"];
            const unit = d["Unit Name"];
            const geo = {
              region: capitalizeFirstLetter(d.Region),
              state: d.State,
              stateID,
              country: country.name,
              countryID: country.id,
              plant,
              plantID: `${plant}-${stateID}`.replace(/ /g, "-"),
              unit,
            };
            if (!geo) {
              console.warn(
                `${d.Country} in investments but not country lookup`
              );
            }
            const metrics = {};
            Object.keys(metricMap).forEach((k) => {
              const metric = metricMap[k];
              if (!d[k]) {
                console.log(k, "missing");
              }
              let value = d[k].trim();
              if (metric.type === "value") {
                value = Number(value);
                if (isNaN(value)) {
                  value = 0;
                }
              } else {
                value = value === "" ? "NA" : value;
              }
              metrics[metric.key] = value;
            });
            const parsedYear = parseInt(
              d["Year of Commission (Acquisition year for M&A)"]
            );
            const year = isNaN(parsedYear) ? "TBD" : parsedYear;
            return {
              ...geo,
              ...metrics,
              year,
              details: d,
              initialIndex,
            };
          }
        );
        this._data.investments = rollups(
          this._data.investments,
          (v) => {
            const parentPlant = { ...v[0] };
            parentPlant.units = v;
            Object.keys(metricMap).forEach((metric) => {
              const key = metricMap[metric].key;
              const type = metricMap[metric].type;
              if (type === "value") {
                parentPlant[key] = v.reduce(
                  (a, b) => Number(a) + Number(b[key]),
                  0
                );
              } else {
                parentPlant[key] = Array.from(new Set(v.map((d) => d[key])))
                  .sort()
                  .join(", ");
              }
              parentPlant.details[metric] = parentPlant[key];
            });
            parentPlant.units.sort((a, b) => a.initialIndex - b.initialIndex);
            return parentPlant;
          },
          (d) => d.plant
        ).map((d) => d[1]);
        // window.investments = this._data.investments;
        // console.log(this._data.investments.length)
        const yearsSet = new Set();
        this._data.investments.forEach((d) => {
          d.units.forEach((unit) => {
            yearsSet.add(unit.year);
          });
        });
        this._data.years = [...yearsSet].sort();
        // console.log(this._data.years)
        this._data.countries = [
          ...new Set(this._data.investments.map((d) => d.country)),
        ].sort((a, b) => (a > b ? 1 : -1));

        this._data.regions = mapToNest(
          group(
            this._data.investments.map((d) => ({
              region: d.region,
              country: d.countryID,
            })),
            (d) => d.region
          )
        )
          .map((r) => {
            r.values = [...new Set(r.values.map((v) => v.country))];
            r.countries = sortBy(
              r.values.map((c) => getCountryById(c)),
              "name"
            );
            return r;
          })
          .sort((a, b) => (a.key > b.key ? 1 : -1));

        resolve(this._data);
      });
    });
  }
}

export default new DataLoader();
